import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const DISCORD_URL = "https://discord.gg/FsFnn9NuF3";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
  { label: "Discord", to: "/discord" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#090a0f]/85 backdrop-blur-xl border-b border-purple-500/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <NavLink to="/" data-testid="navbar-logo-link" className="flex items-center gap-2.5 group">
          <img
            src="/logo.png"
            alt="Vexora Development logo"
            className="w-8 h-8 object-contain drop-shadow-[0_0_10px_rgba(168,85,247,0.5)]"
          />
          <span className="font-display font-bold text-lg tracking-tight text-white">
            Vexora <span className="text-purple-400">Development</span>
          </span>
        </NavLink>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`navbar-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "text-white"
                    : "text-slate-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px w-full bg-purple-400 transition-all duration-300 ${
                      isActive ? "opacity-100 shadow-[0_0_8px_rgba(168,85,247,0.8)]" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:block">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-join-discord-button"
            className="btn-glow inline-flex items-center rounded-lg bg-purple-600 hover:bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_0_22px_rgba(168,85,247,0.4)] hover:shadow-[0_0_32px_rgba(168,85,247,0.65)]"
          >
            Join Discord
          </a>
        </div>

        <button
          type="button"
          data-testid="navbar-mobile-menu-toggle"
          className="md:hidden p-2 text-slate-300 hover:text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-[#090a0f]/95 backdrop-blur-xl border-b border-purple-500/15 px-4 pb-5 pt-2 space-y-1">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "text-purple-300" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-mobile-join-discord-button"
            onClick={() => setOpen(false)}
            className="btn-glow mt-2 flex justify-center rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_22px_rgba(168,85,247,0.4)]"
          >
            Join Discord
          </a>
        </div>
      )}
    </header>
  );
}
